/**
 * ConnectionInfoCard
 * ==================
 * Place at: frontend/src/components/map/ConnectionInfoCard.jsx
 *
 * Extracted from Dashboard.jsx (September 2026). It shares no state with
 * the page — it takes props and renders — so living inside a 1,100-line
 * page component bought nothing and cost reuse.
 *
 * This is the cross-zone payoff moment — the card that says a photograph
 * inspired a robot. useMapController already returns connectionInfo for
 * every map, so any surface can render this; only Dashboard did, because
 * the component was locked inside it.
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, Sparkles } from 'lucide-react';
import { getProjectZone } from '../../config/zones';


export const ConnectionInfoCard = ({ source, target, onClose, onViewProject }) => {
  const [visible, setVisible] = useState(false);
  const timerRef = React.useRef(null);

  const sourceZone = getProjectZone(source);
  const targetZone = getProjectZone(target);
  const isCrossZone = sourceZone.id !== targetZone.id;
  const sourceImage = source.image_url || source.imageUrl;
  const targetImage = target.image_url || target.imageUrl;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    timerRef.current = setTimeout(() => onClose?.(), 6000);
    return () => clearTimeout(timerRef.current);
  }, []);

  const handleClick = (project) => {
    clearTimeout(timerRef.current);
    onViewProject?.(project);
    onClose?.();
  };

  return (
    <div
      className="absolute top-16 left-1/2 z-40 pointer-events-auto"
      style={{
        transform: `translateX(-50%) translateY(${visible ? '0' : '-12px'})`,
        opacity: visible ? 1 : 0,
        transition: 'all 0.4s cubic-bezier(0.19, 1, 0.22, 1)',
        maxWidth: '420px',
        width: 'calc(100vw - 40px)',
      }}
    >
      <div
        className="backdrop-blur-2xl border rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'rgba(9, 9, 11, 0.92)',
          borderColor: isCrossZone ? `${sourceZone.color}20` : 'rgba(63, 63, 70, 0.4)',
          boxShadow: isCrossZone
            ? `0 0 60px ${sourceZone.color}08, 0 0 60px ${targetZone.color}08, 0 20px 40px rgba(0,0,0,0.4)`
            : '0 20px 40px rgba(0,0,0,0.4)',
        }}
      >
        {isCrossZone && (
          <div className="h-[2px]" style={{ background: `linear-gradient(90deg, ${sourceZone.color}80, ${targetZone.color}80)` }} />
        )}
        <div className="p-3.5">
          <div className="flex items-center gap-2.5">
            <button onClick={() => handleClick(source)} className="flex items-center gap-2 flex-1 min-w-0 p-1.5 rounded-lg hover:bg-zinc-800/50 transition-colors">
              <div className="w-9 h-9 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0 border border-zinc-700/30">
                {sourceImage
                  ? <img src={sourceImage} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-sm font-bold" style={{ color: sourceZone.color, backgroundColor: `${sourceZone.color}10` }}>{source.name?.[0]?.toUpperCase()}</div>
                }
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-zinc-200 truncate">{source.name}</p>
                <p className="text-[10px] font-medium" style={{ color: sourceZone.color }}>{sourceZone.shortLabel}</p>
              </div>
            </button>

            <div className="flex-shrink-0 flex items-center">
              {isCrossZone ? (
                <div className="flex items-center gap-0.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: sourceZone.color }} />
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-600" />
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: targetZone.color }} />
                </div>
              ) : (
                <ArrowRight className="w-3.5 h-3.5 text-zinc-700" />
              )}
            </div>

            <button onClick={() => handleClick(target)} className="flex items-center gap-2 flex-1 min-w-0 p-1.5 rounded-lg hover:bg-zinc-800/50 transition-colors">
              <div className="w-9 h-9 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0 border border-zinc-700/30">
                {targetImage
                  ? <img src={targetImage} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-sm font-bold" style={{ color: targetZone.color, backgroundColor: `${targetZone.color}10` }}>{target.name?.[0]?.toUpperCase()}</div>
                }
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-zinc-200 truncate">{target.name}</p>
                <p className="text-[10px] font-medium" style={{ color: targetZone.color }}>{targetZone.shortLabel}</p>
              </div>
            </button>

            <button onClick={onClose} className="p-1 text-zinc-700 hover:text-zinc-400 transition-colors flex-shrink-0">
              <X className="w-3 h-3" />
            </button>
          </div>

          {isCrossZone && (
            <div className="flex items-center justify-center gap-1.5 mt-2 pt-2 border-t border-zinc-800/30">
              <Sparkles className="w-3 h-3 text-fuchsia-400/60" />
              <span className="text-[10px] text-zinc-500">
                Cross-discipline connection &mdash; <span style={{ color: sourceZone.color }}>{sourceZone.shortLabel}</span> inspired <span style={{ color: targetZone.color }}>{targetZone.shortLabel}</span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectionInfoCard;